/**
 * The job header's buttons, ⋯ and meta additions — folder 52, Board 1
 * (`docs/job-stages.md` §1, §3).
 *
 * Nothing here decides a button: `jobActions()` (`shared/src/job-actions.ts`,
 * served by `GET /api/v1/jobs/:id/actions`) says which actions there are and
 * in which slot; this file only draws them — the words from
 * `job-stages/copy.ts`, the glyphs from `job-stages/action-glyphs.tsx`, each
 * where Board 1 puts it:
 *
 * - the **secondary** white and the **primary** green, right-most beside ⋯;
 * - ⋯ — **Edit** first, then the rest in the order drawn, a divider between
 *   the groups `jobActions()` returns, each row a glyph, a label and the
 *   board's one-line sub; today's quiet-record items after them;
 * - on a **phone**, where the header shows only ⋯, its menu lists the
 *   primary first, then the secondary, then the rest (§1);
 * - the meta line's *Q-2184 v2 · sent Oct 6 · viewed Oct 7* (Quoted) and
 *   *<n> of <m> visits booked* (Approved, Scheduled), with the board's glyphs.
 */
import type { JobActionItem, JobActionKey, JobActions, JobActionsAnswer } from '@rowted/shared'
import type { MenuItem } from '../components/context-menu.js'
import type { RecordAction, RecordMeta } from '../record/record-header.js'
import type { QuickAction } from '../record/record-phone.js'
import { META, TOAST, actionLabel, actionSub, quoteVersion, type ActionPage } from '../job-stages/copy.js'
import { StageGlyph, type StageGlyphName } from '../job-stages/action-glyphs.js'
import { ApproveQuote } from '../job-stages/approve-quote.js'
import { MarkQuoted } from '../job-stages/mark-quoted.js'
import { WithdrawQuote } from '../job-stages/withdraw-quote.js'
import { MarkDeclined } from '../job-stages/mark-declined.js'
import { CancelJob } from '../job-stages/cancel-job.js'
import { formatDateOnly } from '../lib/format.js'

/** A ⋯ row's glyph (17 px on Boards 1 and 2) — the quote page's bar reads the same map. */
export const ROW_GLYPH: Record<JobActionKey, StageGlyphName> = {
  edit: 'pencil',
  new_quote: 'plus',
  open_quote: 'doc',
  open_draft: 'doc',
  open_version: 'doc',
  open_agreement: 'doc',
  send: 'send',
  mark_quoted: 'send',
  approve: 'check',
  mark_declined: 'close',
  withdraw_quote: 'rotate',
  cancel_job: 'close',
  reopen_request: 'rotate',
  schedule: 'calendar',
  start_work: 'play',
  complete: 'check',
  preview: 'eye',
  check_crew: 'calendar',
}

/**
 * A button's glyph (14.5 px). Boards 1 and 2 draw one on every green button
 * and on the white *Reopen request* and *Change order*; the white *Cancel job*,
 * *Preview* and *Open the agreement* have none. *Send to Karen* and *Open v2*
 * are Board 2's (the quote page's bar reads this map too).
 */
export const BUTTON_GLYPH: Partial<Record<JobActionKey, StageGlyphName>> = {
  new_quote: 'plus',
  open_quote: 'doc',
  open_version: 'doc',
  send: 'send',
  approve: 'check',
  schedule: 'calendar',
  reopen_request: 'rotate',
}

/** A white or green button for one of `jobActions()`'s items. */
export function stageButton(
  item: JobActionItem, actions: JobActions, onAction: (item: JobActionItem) => void,
): RecordAction {
  const glyph = BUTTON_GLYPH[item.key]
  return {
    label: actionLabel(item, actions.firstName),
    ...(glyph ? { icon: <StageGlyph name={glyph} size={14.5} /> } : {}),
    onClick: () => onAction(item),
  }
}

/** One ⋯ row: Board 1's glyph, label and sub. */
function row(
  item: JobActionItem, actions: JobActions, onAction: (item: JobActionItem) => void,
  options: { key: string; separatorBefore: boolean; sub: boolean; page: ActionPage },
): MenuItem {
  const sub = options.sub
    ? actionSub(item, { page: options.page, firstName: actions.firstName, day: formatDateOnly })
    : null
  return {
    key: options.key,
    label: actionLabel(item, actions.firstName),
    icon: <StageGlyph name={ROW_GLYPH[item.key]} size={17} />,
    ...(sub ? { sub } : {}),
    ...(options.separatorBefore ? { separatorBefore: true } : {}),
    onSelect: () => onAction(item),
  }
}

/**
 * ⋯ — `more`'s groups with a divider between them (Boards 1 and 2); on a
 * phone the primary and then the secondary come first, as the buttons they
 * are on a desktop (no sub — the boards draw none on a button), and the rest
 * follow. `page` picks the sub where the job's and the quote's differ
 * (*another version of Q-2184* / *a copy of v2, as the next version*).
 */
export function stageMenu(
  actions: JobActions, onAction: (item: JobActionItem) => void,
  options: { phone: boolean; page: ActionPage },
): MenuItem[] {
  const items: MenuItem[] = []
  if (options.phone) {
    for (const [slot, item] of [['primary', actions.primary], ['secondary', actions.secondary]] as const) {
      if (item) items.push(row(item, actions, onAction, { key: slot, separatorBefore: false, sub: false, page: options.page }))
    }
  }
  actions.more.forEach((group, index) => {
    group.forEach((item, at) => {
      items.push(row(item, actions, onAction, {
        key: `${index}-${item.key}`,
        separatorBefore: at === 0 && items.length > 0,
        sub: true,
        page: options.page,
      }))
    })
  })
  return items
}

/** The phone's green quick action off the Overview, Activity and Files tabs: `jobActions()`'s primary. */
export function stagePhonePrimary(
  actions: JobActions, onAction: (item: JobActionItem) => void,
): QuickAction | null {
  const item = actions.primary
  if (!item) return null
  return {
    label: actionLabel(item, actions.firstName),
    icon: <StageGlyph name={BUTTON_GLYPH[item.key] ?? ROW_GLYPH[item.key]} size={18} />,
    onClick: () => onAction(item),
  }
}

/**
 * The meta line's two additions (§3): the quote with the customer, while
 * Quoted — a link to it, like every quote number in a meta line (findings
 * §6) — and *<n> of <m> visits booked*, while Approved or Scheduled.
 */
export function stageMeta(actions: JobActions, onOpenQuote: (quoteId: string) => void): RecordMeta[] {
  const out: RecordMeta[] = []
  const quote = actions.meta.withCustomer
  if (quote?.sentOn) {
    out.push({
      icon: <StageGlyph name="send" size={13} />,
      text: META.withCustomer(
        quoteVersion(quote.reference, quote.version),
        formatDateOnly(quote.sentOn),
        quote.viewedOn ? formatDateOnly(quote.viewedOn) : null),
      onClick: () => onOpenQuote(quote.id),
    })
  }
  /*
   * Only when the job has a visit to book: `total` counts the visits that
   * aren't cancelled (`visitsBooked`, server), so a job whose only visit was
   * cancelled says nothing rather than *0 of 0 visits booked*.
   */
  const visits = actions.meta.visitsBooked
  if (visits && visits.total > 0) {
    out.push({
      icon: <StageGlyph name="truck" size={13} />,
      text: META.visitsBooked(visits.booked, visits.total),
    })
  }
  return out
}

/**
 * Board 3's popups, opened from the job's buttons and ⋯ — A Approve, B Mark
 * quoted, D Withdraw quote, E Mark declined, F Cancel job (C52a's, in
 * `job-stages/`). Each one does its own call; `onDone` reloads the job and
 * hands back the white toast for the move (findings §11, lead's decision):
 * §14's History sentence — *Q-2184 v2 approved by Karen Whitmer* · *… marked
 * quoted* · *… withdrawn* · *… declined* — and none for Cancel job, whose
 * chip and bands say it.
 */
export function JobStagePopups({
  jobId, item, actions, phone, onClose, onDone,
}: {
  jobId: string
  /** The button or ⋯ row that opened one; null, none is open. */
  item: JobActionItem | null
  /** `GET /jobs/:id/actions` — its quotes name the one a popup acted on. */
  actions: JobActionsAnswer | null
  phone: boolean
  onClose: () => void
  onDone: (toast: string | null) => void
}) {
  if (!item) return null
  /** *Q-2184 v2*, for the quote a popup acted on. */
  const named = (quoteId: string): string | null => {
    const quote = actions?.quotes.find((one) => one.id === quoteId)
      ?? (item.quote?.id === quoteId ? item.quote : null)
    return quote ? quoteVersion(quote.reference, quote.version) : null
  }
  const quoteId = item.quote?.id ?? null
  switch (item.key) {
    case 'approve':
      return quoteId ? (
        <ApproveQuote
          open
          quoteId={quoteId}
          phone={phone}
          onClose={onClose}
          onDone={(answer) => {
            const quote = named(quoteId)
            const who = answer.approvedByName
            onDone(quote && who ? TOAST.approved(quote, who) : null)
          }}
        />
      ) : null
    case 'mark_quoted':
      return (
        <MarkQuoted
          open
          jobId={jobId}
          phone={phone}
          onClose={onClose}
          onDone={(answer) => {
            const quote = named(answer.quoteId)
            onDone(quote ? TOAST.markedQuoted(quote) : null)
          }}
        />
      )
    case 'withdraw_quote':
      return quoteId ? (
        <WithdrawQuote
          open
          jobId={jobId}
          quoteId={quoteId}
          phone={phone}
          onClose={onClose}
          onDone={(answer) => {
            const quote = named(answer.quoteId)
            onDone(quote ? TOAST.withdrawn(quote) : null)
          }}
        />
      ) : null
    case 'mark_declined':
      return quoteId ? (
        <MarkDeclined
          open
          jobId={jobId}
          quoteId={quoteId}
          phone={phone}
          onClose={onClose}
          onDone={(answer) => {
            const quote = named(answer.quoteId)
            onDone(quote ? TOAST.declined(quote) : null)
          }}
        />
      ) : null
    case 'cancel_job':
      return <CancelJob open jobId={jobId} phone={phone} onClose={onClose} onDone={() => onDone(null)} />
    default:
      return null
  }
}
